import * as React from 'react';
import { withStyles, StyleRulesCallback, WithStyles } from '@material-ui/core/styles';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import MyAppBar from '../navigation/MyAppBar';
import SelfActual from '../pages/assessment/self-assessment/SelfActual';
import SelfHistory from '../pages/assessment/self-assessment/SelfHistory';
import ExternalActualList from '../pages/assessment/external-assessment/ExternalActualList';
import ExternalHistoryList from '../pages/assessment/external-assessment/ExternalHistoryList';
import Employees from '../pages/user/Employees';
import MyDrawer from '../navigation/MyDrawer';
import { observer } from 'mobx-react';
import { IObservableValue, observable } from 'mobx';
import { EmployeeDto, Roles } from '../pages/user/userTypes';
import RestrictedRoute from '../components/RestrictedRoute';
import Questionnaires from '../pages/questionnaire/Questionnaires';
import QuestionCatalogueList from '../pages/questionCatalogue/QuestionCatalogueList';
import EvaluationList from '../pages/evaluation/EvaluationList';
import Evaluation from '../pages/evaluation/Evaluation';
import NotFoundPage from '../pages/error/NotFoundPage';
import ExternalActual from '../pages/assessment/external-assessment/ExternalActual';
import ExternalHistory from '../pages/assessment/external-assessment/ExternalHistory';
import {
  CATALOGUE_ROUTE,
  CREATE_CATALOGUE_ROUTE,
  EMPLOYEES_ROUTE,
  EVALUATION_LIST_ROUTE,
  EXTERNAL_ACTUAL_LIST_ROUTE,
  EXTERNAL_HISTORY_LIST_ROUTE,
  QUESTIONNAIRE_ROUTE,
  SELF_ACTUAL_ROUTE,
  SELF_HISTORY_LIST_ROUTE
} from './routes';
import CreateCatalogue from '../pages/questionCatalogue/CreateCatalogue';
import UnknownUserPage from '../pages/error/UnknownUserPage';

type LocalStyles = 'page' | 'pageWithoutDrawer' | 'content';

interface Props {
  currentAccount: EmployeeDto
}

const localStyles: StyleRulesCallback<LocalStyles> = theme => ({
  page: {
    height: '100%',
    width: '100%',
    zIndex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row'
  },
  pageWithoutDrawer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
  content: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing.unit,
    width: '100%',
    height: '100%',
    overflow: 'auto',
  },
});

type PropsWithStyle = WithStyles<LocalStyles> & Props;

@observer
class MyRouter extends React.Component<PropsWithStyle> {

  private mobileOpen: IObservableValue<boolean> = observable.box(false);

  @observable
  private showSearch: boolean = false;

  @observable
  private searchText: IObservableValue<string> = observable.box('');

  @observable
  private currentRouteIndex: IObservableValue<number> = observable.box(-1);

  constructor(props: PropsWithStyle) {
    super(props);
    this.handleDrawerToggle = this.handleDrawerToggle.bind(this);
  }

  public render() {
    const {classes} = this.props;
    return (
      <>
        {
          this.props.currentAccount.id.length === 0 ?
            <UnknownUserPage/> :
            <BrowserRouter>
              <div className={classes.page}>
                <MyDrawer selfAccount={this.props.currentAccount} mobileOpen={this.mobileOpen.get()}
                          resetSearch={this.resetSearch}
                          handleDrawerToggle={this.handleDrawerToggle}
                          currentRouteIndex={this.currentRouteIndex}
                />
                <div className={classes.pageWithoutDrawer}>
                  <MyAppBar handleDrawerToggle={this.handleDrawerToggle}
                            getSearchText={this.getSearchText} showSearch={this.showSearch}
                            searchText={this.searchText}/>
                  <main className={classes.content}>
                    <Switch>
                      <Route path={SELF_ACTUAL_ROUTE.route} exact={true}
                             render={() => {
                               this.currentRouteIndex.set(SELF_ACTUAL_ROUTE.index);
                               return (<SelfActual currentEmployee={this.props.currentAccount!!}
                                                   renderSearch={this.renderSearch}/>)
                             }}/>
                      <Route path={SELF_HISTORY_LIST_ROUTE.route} exact={true}
                             render={() => {
                               this.currentRouteIndex.set(SELF_HISTORY_LIST_ROUTE.index);
                               return (<SelfHistory searchText={this.searchText} renderSearch={this.renderSearch}/>);
                             }}/>
                      <Route path={SELF_HISTORY_LIST_ROUTE.route + '/:questionnaireId'} exact={true}
                             render={props => {
                               this.currentRouteIndex.set(SELF_HISTORY_LIST_ROUTE.index);
                               return (<Evaluation enableComment={false} renderSearch={this.renderSearch}
                                                   questionnaireId={Number(props.match.params.questionnaireId)}/>);
                             }}/>
                      <Route path={EXTERNAL_ACTUAL_LIST_ROUTE.route} exact={true}
                             render={() => {
                               this.currentRouteIndex.set(EXTERNAL_ACTUAL_LIST_ROUTE.index);
                               return (<ExternalActualList searchText={this.searchText}
                                                           currentEmployee={this.props.currentAccount!!}
                                                           renderSearch={this.renderSearch}/>)
                             }}/>
                      <Route path={EXTERNAL_ACTUAL_LIST_ROUTE.route + '/:questionnaireId'} exact={true}
                             render={props => {
                               this.currentRouteIndex.set(EXTERNAL_ACTUAL_LIST_ROUTE.index);
                               return (<ExternalActual questionnaireId={Number(props.match.params.questionnaireId)}
                                                       currentEmployee={this.props.currentAccount!!}
                                                       renderSearch={this.renderSearch}/>);
                             }}/>
                      <Route path={EXTERNAL_HISTORY_LIST_ROUTE.route} exact={true}
                             render={() => {
                               this.currentRouteIndex.set(EXTERNAL_HISTORY_LIST_ROUTE.index);
                               return (<ExternalHistoryList searchText={this.searchText}
                                                            renderSearch={this.renderSearch}/>)
                             }}/>
                      <Route path={EXTERNAL_HISTORY_LIST_ROUTE.route + '/:questionnaireId'} exact={true}
                             render={props => {
                               this.currentRouteIndex.set(EXTERNAL_HISTORY_LIST_ROUTE.index);
                               return (<ExternalHistory questionnaireId={Number(props.match.params.questionnaireId)}
                                                        renderSearch={this.renderSearch}/>);
                             }}/>
                      <RestrictedRoute updateCurrentIndex={this.updateCurrentRouteIndex}
                                       minRole={Roles.ADMIN}
                                       currentAccount={this.props.currentAccount} path={EMPLOYEES_ROUTE.route} exact={true}
                                       render={() => {
                                    this.currentRouteIndex.set(EMPLOYEES_ROUTE.index);
                                    return (<Employees searchText={this.searchText} renderSearch={this.renderSearch}/>);
                                  }}/>
                      <RestrictedRoute updateCurrentIndex={this.updateCurrentRouteIndex}
                                       currentAccount={this.props.currentAccount} path={QUESTIONNAIRE_ROUTE.route}
                                       minRole={Roles.MAINTAINER}
                                       exact={true}
                                       render={() => {
                                    this.currentRouteIndex.set(QUESTIONNAIRE_ROUTE.index);
                                    return (<Questionnaires searchText={this.searchText}
                                                            renderSearch={this.renderSearch}/>);
                                  }}/>
                      <RestrictedRoute updateCurrentIndex={this.updateCurrentRouteIndex}
                                       currentAccount={this.props.currentAccount} path={CATALOGUE_ROUTE.route} exact={true}
                                       minRole={Roles.MAINTAINER}
                                       render={() => {
                                    this.currentRouteIndex.set(CATALOGUE_ROUTE.index);
                                    return (<QuestionCatalogueList searchText={this.searchText}
                                                                   renderSearch={this.renderSearch}/>);
                                  }}/>
                      <RestrictedRoute updateCurrentIndex={this.updateCurrentRouteIndex}
                                       currentAccount={this.props.currentAccount} path={CREATE_CATALOGUE_ROUTE.route}
                                       exact={true}
                                       minRole={Roles.MAINTAINER}
                                       render={() => {
                                    this.currentRouteIndex.set(CREATE_CATALOGUE_ROUTE.index);
                                    return (<CreateCatalogue searchText={this.searchText}
                                                             resetSearch={this.resetSearch}
                                                             renderSearch={this.renderSearch}/>);
                                  }}/>
                      <RestrictedRoute updateCurrentIndex={this.updateCurrentRouteIndex}
                                       currentAccount={this.props.currentAccount} path={EVALUATION_LIST_ROUTE.route}
                                       exact={true}
                                       minRole={Roles.ADMIN}
                                       render={() => {
                                    this.currentRouteIndex.set(EVALUATION_LIST_ROUTE.index);
                                    return (<EvaluationList searchText={this.searchText}
                                                            renderSearch={this.renderSearch}/>);
                                  }}/>
                      <RestrictedRoute updateCurrentIndex={this.updateCurrentRouteIndex}
                                       currentAccount={this.props.currentAccount}
                                       exact={true}
                                       minRole={Roles.ADMIN}
                                       path={EVALUATION_LIST_ROUTE.route + '/:questionnaireId'}
                                       render={props => {
                                    this.currentRouteIndex.set(EVALUATION_LIST_ROUTE.index);
                                    return (<Evaluation enableComment={true} renderSearch={this.renderSearch}
                                                        questionnaireId={props.match.params.questionnaireId}/>);
                                  }}/>
                      <Route render={() => {
                        this.currentRouteIndex.set(-1);
                        return (<NotFoundPage/>);
                      }}/>
                    </Switch>
                  </main>
                </div>
              </div>
            </BrowserRouter>
        }
      </>
    );
  }

  private handleDrawerToggle = () => {
    this.mobileOpen.set(!this.mobileOpen.get())
  };

  private renderSearch = (showSearch: boolean) => {
    this.showSearch = showSearch
  };

  private getSearchText = (searchText: string) => {
    this.searchText.set(searchText);
  };

  private resetSearch = () => {
    this.searchText.set('')
  };

  private updateCurrentRouteIndex = (currentRouteIndex: number) => {
    this.currentRouteIndex.set(currentRouteIndex);
  };
}

export default withStyles(localStyles, {withTheme: true})(MyRouter);
